// 名前変更 popover の共通モジュール。/、/club、/lite で同じ UX を提供する。
//
// Web 標準を活用して JS は最小限に:
//   - <button popovertarget="..."> で popover 開閉を宣言的に
//   - <div popover="auto"> で ESC + outside click の light-dismiss を browser に任せる
//   - キャンセル button は popovertargetaction="hide" で同じく宣言的に閉じる
//   - CSS の anchor-name / position-anchor で button 直下に anchor (= browser 対応版)
//   - @supports not (anchor-name) で Firefox / 旧 Safari は fixed position fallback
//
// JS の責務はこれだけ:
//   1. open 時に pre-fill (= toggle event で newState 監視)
//   2. submit handler: logout + login + reload
//
// HTML 側の要素 ID (= 各 page で同じ markup を共有):
//   #name-change-btn         topbar の起動 button (= popovertarget=...)
//   #name-change-popover     popover 本体 (= popover="auto")
//   #name-change-name        新 name input
//   #name-change-aikotoba    合言葉 input
//   #name-change-submit      変更 button (= JS で submit 処理)
//   #name-change-error       error 表示

/** 現在の verified name を渡して popover を初期化する。
 *  call は idempotent (= 2 度呼ぶと event listener が重複するので注意)。 */
export function setupNameChangeModal(currentName: string): void {
  const btn = document.getElementById("name-change-btn") as HTMLButtonElement | null;
  const popover = document.getElementById("name-change-popover") as HTMLElement | null;
  const nameInput = document.getElementById("name-change-name") as HTMLInputElement | null;
  const aikotobaInput = document.getElementById("name-change-aikotoba") as HTMLInputElement | null;
  const submitBtn = document.getElementById("name-change-submit") as HTMLButtonElement | null;
  const errorEl = document.getElementById("name-change-error") as HTMLElement | null;
  if (!btn || !popover || !nameInput || !aikotobaInput || !submitBtn || !errorEl) {
    return;
  }
  btn.hidden = false;

  // popover の open/close は browser が行うので、toggle event で「開いた時の pre-fill」
  // だけ JS で処理する。close は popover="auto" の light-dismiss + cancel button の
  // popovertargetaction="hide" で完結する。
  popover.addEventListener("toggle", (ev) => {
    const e = ev as ToggleEvent;
    if (e.newState === "open") {
      nameInput.value = currentName;
      aikotobaInput.value = "";
      errorEl.textContent = "";
      submitBtn.disabled = false;
      // pre-fill した name は全選択にしておく (= すぐ書き換えやすい)
      queueMicrotask(() => {
        nameInput.focus();
        nameInput.select();
      });
    }
  });

  submitBtn.addEventListener("click", async () => {
    const newName = nameInput.value.trim();
    const aikotoba = aikotobaInput.value;
    if (!newName) {
      errorEl.textContent = "名前を入力してください";
      return;
    }
    if (!aikotoba) {
      errorEl.textContent = "合言葉を入力してください";
      return;
    }
    if (newName === currentName) {
      // 同じ名前なら何もせず close (= 「変更がない」 → 操作不要)
      popover.hidePopover?.();
      return;
    }
    submitBtn.disabled = true;
    errorEl.textContent = "";
    try {
      // 既存 session を明示的に切る (= 失敗しても新 login で上書きされるが、cookie state
      // を clean に保つ意味で必ず叩く)
      await fetch("/api/logout", { method: "POST", credentials: "same-origin" });
      const res = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ name: newName, aikotoba }),
      });
      if (res.status === 401) {
        errorEl.textContent = "合言葉が違います";
        submitBtn.disabled = false;
        return;
      }
      if (!res.ok) {
        errorEl.textContent = `エラー (${res.status})`;
        submitBtn.disabled = false;
        return;
      }
      // 成功: reload で全 state (= WS / participants / avatar / 表示名) を再初期化。
      // page-specific な name change cleanup を持たなくて済む最速ルート。
      location.reload();
    } catch {
      errorEl.textContent = "通信エラー";
      submitBtn.disabled = false;
    }
  });
}
